import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { differenceInDays, startOfDay } from 'date-fns';

export interface Streak {
  id: string;
  user_id: string;
  baby_id: string | null;
  current_streak: number;
  longest_streak: number;
  last_log_date: string | null;
  total_logs: number;
}

export function useStreaks(babyId?: string) {
  const { user } = useAuth();
  const [streak, setStreak] = useState<Streak | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchStreak();

    // Subscribe to changes
    const channel = supabase
      .channel(`streak-changes-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_streaks',
          filter: `user_id=eq.${user.id}${babyId ? ` AND baby_id=eq.${babyId}` : ''}`,
        },
        () => {
          fetchStreak();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, babyId]);

  const fetchStreak = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', user.id);

      if (babyId) {
        query = query.eq('baby_id', babyId);
      } else {
        query = query.is('baby_id', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      setStreak(data);
    } catch (error) {
      console.error('Error fetching streak:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStreak = async () => {
    if (!user) return null;

    try {
      const previous = streak;
      const { data, error } = await supabase.rpc('increment_streak', {
        _baby_id: babyId ?? null,
      });

      if (error) throw error;
      if (!data) return null;

      const updated = data as Streak;
      setStreak(updated);

      const wasStreakBroken =
        !!previous && updated.current_streak === 1 && previous.current_streak > 1;
      const isNewStreak = !previous || wasStreakBroken;

      return {
        isNewStreak,
        streak: updated.current_streak,
        wasStreakBroken,
        milestoneMet: [7, 14, 30, 50, 100].includes(updated.current_streak),
      };
    } catch (error) {
      console.error('Error updating streak:', error);
      return null;
    }
  };

  return {
    streak,
    isLoading,
    updateStreak,
    refetch: fetchStreak,
  };
}
